'use client';

import { AspectRatio } from '@/app/(webapp)/_components/ui/aspect-ratio';
import { Skeleton } from '@/app/(webapp)/_components/ui/skeleton';
import { LanguageIsoCode } from '@/graphql/graphql';
import { cn } from '@/lib/utils';
import PlaceholderImage from '@public/images/program_placeholder.svg';
import Image from 'next/image';
import { useEffect, useState } from 'react';

const loadedCoverImageSrcs = new Set<string>();

interface CoverImageProgram {
    coverUrl?: string | null;
    titles?: Record<string, string> | null;
    editorialization?: { title?: string | null } | null;
    parent?: {
        coverUrl?: string | null;
        titles?: Record<string, string> | null;
        editorialization?: { title?: string | null } | null;
    } | null;
}

export default function CoverImage({
    program,
    language,
    className,
    imageUrl,
    alt,
    fallbackTitle = false,
}: {
    program: CoverImageProgram;
    language?: LanguageIsoCode;
    imageUrl?: string;
    className?: string;
    alt?: string;
    fallbackTitle?: boolean;
}) {
    const coverUrl = imageUrl ?? program.coverUrl ?? program?.parent?.coverUrl ?? PlaceholderImage.src;
    const coverImageSrc = coverUrl;
    const [isLoaded, setIsLoaded] = useState(() => loadedCoverImageSrcs.has(coverImageSrc));
    const locale = language ? language.toLowerCase() : LanguageIsoCode.En.toLowerCase();

    useEffect(() => {
        setIsLoaded(loadedCoverImageSrcs.has(coverImageSrc));
    }, [coverImageSrc]);

    const fallbackText =
        program.titles?.[locale as LanguageIsoCode] ??
        program.editorialization?.title ??
        program.parent?.titles?.[locale as LanguageIsoCode] ??
        program.parent?.editorialization?.title ??
        '';

    const handleImageLoaded = () => {
        loadedCoverImageSrcs.add(coverImageSrc);
        setIsLoaded(true);
    };

    return (
        <div className={cn('relative', className)}>
            <AspectRatio ratio={3 / 4} className="h-full w-full">
                {!isLoaded ? <Skeleton className="absolute inset-0 rounded-lg bg-overlay-white-10" /> : null}
                <Image
                    src={coverImageSrc}
                    alt={alt ?? ''}
                    fill
                    className={cn(
                        'object-cover rounded-lg transition-opacity duration-200',
                        isLoaded ? 'opacity-100' : 'opacity-0',
                        program?.coverUrl ? 'border border-border-muted' : null,
                    )}
                    onLoad={handleImageLoaded}
                    onError={handleImageLoaded}
                />
                {fallbackTitle && !(program?.coverUrl ?? program?.parent?.coverUrl) ? (
                    <div
                        className={cn(
                            'absolute inset-0 flex items-center justify-center transition-opacity duration-200',
                            isLoaded ? 'opacity-100' : 'opacity-0',
                        )}
                    >
                        <p className="line-clamp-3 text-[8cqw] font-bold text-content-on-dark text-center px-2 pt-[65%]">
                            {fallbackText}
                        </p>
                    </div>
                ) : null}
            </AspectRatio>
        </div>
    );
}
